import { useEventStore, useGuestStore } from "store";
import { useEffect, useMemo, useState } from "react";
import { FeedModal } from "./FeedModal";
import { FormattedMessage } from "react-intl";
import { Close, User } from "assets/svg";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { RankingEntry } from "cdt-app-public-api/dist/types";
import CdtAppPublicApiClient from "cdt-app-public-api/dist";

const POINTS_TABLE = [
  {
    action: "Enviar foto",
    points: 1,
  },
  {
    action: "Enviar vídeo",
    points: 3,
  },
  {
    action: "Postar no feed",
    points: 3,
  },
  {
    action: "Completar Quiz",
    points: 5,
  },
  {
    action: "Completar Batalha dos Noivos",
    points: 5,
  },
  {
    action: "Cada Desafio de fotos enviado",
    points: 5,
  },
];

function RankingPointsTable() {
  const { theme } = useEventStore();

  return (
    <div className={`${theme.card} card gap-2 py-3 px-4 flex flex-col`}>
      <h2 className="text-base font-black">Como juntar pontos?</h2>
      <div className="flex flex-col gap-1">
        {POINTS_TABLE.map((item) => (
          <div className="flex justify-between items-center" key={item.action}>
            <h4 className="text-xs">{item.action}</h4>
            <p className="text-xs font-bold">{item.points}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

interface RankingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RankingModal({ isOpen, onClose }: RankingModalProps) {
  const { theme, event } = useEventStore();
  const [guestPhotoUrl, setGuestPhotoUrl] = useState<string | null>(null);
  const cdtAppPublicApiClient = useMemo(() => new CdtAppPublicApiClient(), []);
  const [rankingData, setRankingData] = useState<
    RankingEntry | null | undefined
  >(null);
  const { guest } = useGuestStore();

  useEffect(() => {
    const fetchGuestPhoto = async () => {
      try {
        const url = await SpacesService.getFileUrl(
          guest?.photoStoragePath as string
        );
        setGuestPhotoUrl(url);
      } catch (error) {
        console.error("Error fetching guest photo:", error);
      }
    };

    const fetchRankingData = async () => {
      try {
        const data = await cdtAppPublicApiClient.getGuestRanking(
          event?.code as string,
          guest?.id as string
        );
        setRankingData(data);
      } catch (error) {
        console.error("Error fetching ranking data:", error);
      }
    };

    if (guest?.photoStoragePath) {
      fetchGuestPhoto();
    }

    if (event?.code && guest?.id && isOpen) {
      fetchRankingData();
    }
  }, [guest, event?.code, guest?.id, cdtAppPublicApiClient, isOpen]);

  // User has no points yet
  if (!rankingData?.score || rankingData.score === 0) {
    return (
      <FeedModal isOpen={isOpen} onClose={onClose}>
        <header className="h-8 flex items-center justify-center mt-3">
          <h2 className="font-century-gothic-bold text-lg">
            <FormattedMessage
              id="feed.ranking.title"
              defaultMessage="Ranking da Festa"
            />
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-0 z-10"
          >
            <Close width={24} height={24} color="none" outline="#414141" />
          </button>
        </header>
        <div className="flex-1 flex flex-col p-6 gap-6">
          <div className={`${theme.card} card py-3 px-4`}>
            <h2 className="text-lg text-center font-century-gothic">
              Você ainda não está no ranking
            </h2>
          </div>

          <div className="inline-flex justify-center items-center gap-2">
            <div className="w-10 h-10 grow-0 shrink-0 rounded-full overflow-hidden border-2 bg-white border-gray-200">
              {guestPhotoUrl ? (
                <img
                  src={guestPhotoUrl}
                  alt={guest?.name || "Guest"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User width={14} height={14} color="none" outline="#414141" />
                </div>
              )}
            </div>
            <h4 className="text-base font-century-gothic-bold">
              {guest?.name || "Anônimo"}
            </h4>
          </div>

          <RankingPointsTable />

          <div className="px-4">
            <h3 className="text-base font-century-gothic text-center">
              Comece a participar das atividades e acumule pontos para entrar no
              ranking!
            </h3>
          </div>
        </div>
      </FeedModal>
    );
  }

  // Default: User is in the ranking
  return (
    <FeedModal isOpen={isOpen} onClose={onClose}>
      <header className="h-8 flex items-center justify-center mt-3">
        <h2 className="font-century-gothic-bold text-lg">
          <FormattedMessage
            id="feed.ranking.title"
            defaultMessage="Ranking da Festa"
          />
        </h2>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-0 z-10"
        >
          <Close width={24} height={24} color="none" outline="#414141" />
        </button>
      </header>
      <div className="flex-1 flex flex-col p-6 gap-6">
        <div className={`${theme.card} card py-2 px-4`}>
          <h2 className="text-lg text-center font-century-gothic">
            Você está em{" "}
            <span className="font-century-gothic-bold">
              {rankingData?.rank}º lugar
            </span>
            !
          </h2>
        </div>
        <div className="inline-flex justify-between items-center">
          <div className="inline-flex gap-2 items-center">
            <div className="w-10 h-10 grow-0 shrink-0 rounded-full overflow-hidden border-2 bg-white border-gray-200">
              {guestPhotoUrl ? (
                <img
                  src={guestPhotoUrl}
                  alt={guest?.name || "Guest"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User width={14} height={14} color="none" outline="#414141" />
                </div>
              )}
            </div>
            <h4 className="text-base font-century-gothic-bold">
              {guest?.name || "Anônimo"}
            </h4>
          </div>
          <p className="text-base font-century-gothic-bold">
            {rankingData?.score} {rankingData?.score === 1 ? "ponto" : "pontos"}
          </p>
        </div>

        <RankingPointsTable />
        <div className="px-4">
          <h3 className="text-base font-century-gothic text-center">
            Continue acumulando pontos para{" "}
            {rankingData?.rank === 1
              ? "se manter no topo!"
              : "subir no ranking!"}
          </h3>
        </div>
      </div>
    </FeedModal>
  );
}
